import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── LOGO URLS ────────────────────────────────────────────────────────────────
const nishSrc  = new URL('../../assets/images/NISHlogo.png',  import.meta.url).href;
const ncahtSrc = new URL('../../assets/images/NCAHTlogo.png', import.meta.url).href;
const icmrSrc  = new URL('../../assets/images/ICMRlogo.png',  import.meta.url).href;

async function imgToBase64(src) {
  try {
    const res  = await fetch(src);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const LANGUAGES = [
  { code: "en", name: "English"  },
  { code: "ml", name: "മലയാളം" },
  { code: "hi", name: "हिंदी"   },
  { code: "ta", name: "தமிழ்"  },
  { code: "te", name: "తెలుగు" },
];

const LAYOUTS = [
  { id: "image-top",    label: "Image Top",    icon: "🖼️↑📝", desc: "Image on top, text below"    },
  { id: "image-bottom", label: "Image Bottom", icon: "📝↑🖼️", desc: "Text on top, image below"    },
  { id: "image-left",   label: "Image Left",   icon: "🖼️|📝", desc: "Image left, text right"     },
  { id: "image-right",  label: "Image Right",  icon: "📝|🖼️", desc: "Text left, image right"     },
  { id: "image-full",   label: "Full Image",   icon: "🖼️",    desc: "Full bleed image with overlay" },
  { id: "text-only",    label: "Text Only",    icon: "📝",    desc: "No image — poetry or title"  },
];

const THEMES = [
  { id: "storybook", label: "Storybook",  bg: "#FFFDF5", page: "#FFFFFF", accent: "#8B4513", text: "#3a2010", border: "#D4A96A", titleFont: "Georgia, serif" },
  { id: "ocean",     label: "Ocean",      bg: "#EEF8FF", page: "#FFFFFF", accent: "#1565C0", text: "#0d2a4a", border: "#90CAF9", titleFont: "Georgia, serif" },
  { id: "forest",    label: "Forest",     bg: "#F0FFF4", page: "#FFFFFF", accent: "#2E7D32", text: "#1b3a1c", border: "#A5D6A7", titleFont: "Georgia, serif" },
  { id: "night",     label: "Night Sky",  bg: "#1a1a2e", page: "#16213e", accent: "#E8C84A", text: "#e8e8f0", border: "#4a4a8a", titleFont: "Georgia, serif" },
  { id: "candy",     label: "Candy",      bg: "#FFF0F8", page: "#FFFFFF", accent: "#C2185B", text: "#4a0020", border: "#F48FB1", titleFont: "Georgia, serif" },
  { id: "sunshine",  label: "Sunshine",   bg: "#FFFDE7", page: "#FFFFFF", accent: "#F57F17", text: "#3e2000", border: "#FFD54F", titleFont: "Georgia, serif" },
];

const FONT_SIZES = [
  { id: "sm", label: "Small",   size: 13 },
  { id: "md", label: "Medium",  size: 17 },
  { id: "lg", label: "Large",   size: 22 },
  { id: "xl", label: "X-Large", size: 28 },
];

// ─── EMPTY STORY ──────────────────────────────────────────────────────────────
const EMPTY_STORY = {
  title: "",
  subtitle: "",
  author: "NISH Storybook Series",
  language: "en",
  theme: "storybook",
  layout: "image-top",
  fontSize: "md",
  showPageNumbers: true,
  indicators: [], // book-level NCF development indicator tags
  pages: [
    { id: "p1", layout: "image-top", imageUrl: "", indicators: [], texts: { en: "", ml: "", hi: "", ta: "", te: "" } },
  ],
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function resolveImgUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("/uploads")) return `${API.replace("/api","")}${imageUrl}`;
  return imageUrl;
}

// ─── PDF BUILDER ──────────────────────────────────────────────────────────────
function buildPageHTML(page, pageNum, totalPages, story, themeObj, fontPx, logos = {}) {
  const text    = page.texts?.[story.language] || page.texts?.en || "";
  const layout  = page.layout || story.layout;
  const imgSrc  = resolveImgUrl(page.imageUrl);
  const { page: bg, accent, text: tc, border } = themeObj;

  const logoImg = (src, h, alt) => src
    ? `<img src="${src}" style="height:${h}px;width:auto;object-fit:contain;display:block;" alt="${alt}"/>`
    : `<span style="font-size:7px;color:#003366;font-weight:700;">${alt}</span>`;

  const imgBlock = (w, h) => imgSrc ? `
    <div style="width:${w}px;height:${h}px;flex-shrink:0;border:3px solid ${border};border-radius:10px;overflow:hidden;background:#f8f8f8;">
      <img src="${imgSrc}" style="width:100%;height:100%;object-fit:contain;display:block;" crossorigin="anonymous"/>
    </div>` : "";

  const txtBlock = (maxW) => `
    <div style="font-family:Georgia,serif;font-size:${fontPx}px;line-height:1.75;color:${tc};
                ${maxW?`max-width:${maxW}px;`:""}text-align:justify;padding:6px 0;">
      ${text}
    </div>`;

  let body = "";
  if (layout === "image-top")         body = `<div style="flex:1;display:flex;flex-direction:column;justify-content:center;gap:18px;">${imgBlock(490,300)}${txtBlock(490)}</div>`;
  else if (layout === "image-bottom") body = `<div style="flex:1;display:flex;flex-direction:column;justify-content:center;gap:18px;">${txtBlock(490)}${imgBlock(490,300)}</div>`;
  else if (layout === "image-left")   body = `<div style="flex:1;display:flex;flex-direction:row;align-items:center;gap:20px;">${imgBlock(230,300)}${txtBlock(240)}</div>`;
  else if (layout === "image-right")  body = `<div style="flex:1;display:flex;flex-direction:row;align-items:center;gap:20px;">${txtBlock(240)}${imgBlock(230,300)}</div>`;
  else if (layout === "image-full")   body = `
    <div style="flex:1;position:relative;border:3px solid ${border};border-radius:10px;overflow:hidden;">
      ${imgSrc?`<img src="${imgSrc}" style="width:100%;height:100%;object-fit:cover;display:block;" crossorigin="anonymous"/>`:""}
      <div style="position:absolute;bottom:0;left:0;right:0;background:rgba(0,0,0,0.55);padding:18px 20px;">
        <div style="font-family:Georgia,serif;font-size:${fontPx}px;line-height:1.65;color:white;text-align:center;">${text}</div>
      </div>
    </div>`;
  else body = `<div style="flex:1;display:flex;align-items:center;justify-content:center;"><div style="font-family:Georgia,serif;font-size:${fontPx+4}px;line-height:1.85;color:${tc};text-align:center;max-width:440px;font-style:italic;">${text}</div></div>`;

  return `
  <div style="width:595px;height:842px;background:${bg};display:flex;flex-direction:column;
              padding:26px 32px 20px;box-sizing:border-box;page-break-after:always;">

    <!-- HEADER -->
    <div style="display:flex;align-items:center;justify-content:space-between;
                border-bottom:2px solid ${border};padding-bottom:9px;margin-bottom:14px;flex-shrink:0;">
      <div style="display:flex;align-items:center;gap:8px;">
        ${logoImg(logos.nish, 30, "NISH")}
        <div style="font-family:Arial;font-size:6.5px;color:#555;line-height:1.4;">
          <div style="font-weight:700;color:#003366;">National Institute of Speech &amp; Hearing</div>
          <div>Thiruvananthapuram, Kerala · FSLP</div>
        </div>
      </div>
      <div style="font-family:Georgia,serif;font-size:10px;font-style:italic;color:${accent};font-weight:700;">${story.title}</div>
      <div style="display:flex;align-items:center;gap:6px;">
        ${logoImg(logos.ncaht, 24, "NCAHT")}
        ${logoImg(logos.icmr,  24, "ICMR")}
      </div>
    </div>

    ${body}

    <!-- FOOTER -->
    <div style="border-top:1.5px solid ${border};padding-top:8px;margin-top:10px;flex-shrink:0;
                display:flex;justify-content:space-between;align-items:center;height:40px;">
      <div style="display:flex;align-items:center;gap:7px;">
        ${logoImg(logos.nish,  22, "NISH")}
        ${logoImg(logos.ncaht, 18, "NCAHT")}
        ${logoImg(logos.icmr,  18, "ICMR")}
      </div>
      ${story.showPageNumbers?`<div style="font-family:Arial;font-size:8px;color:${accent};font-weight:700;">${pageNum} / ${totalPages}</div>`:""}
    </div>
  </div>`;
}

function buildCoverHTML(story, themeObj, logos = {}) {
  const { bg, page, accent, border, titleFont } = themeObj;
  const firstImg = resolveImgUrl(story.pages[0]?.imageUrl);

  const logoImg = (src, h, alt) => src
    ? `<img src="${src}" style="height:${h}px;width:auto;object-fit:contain;display:block;" alt="${alt}"/>`
    : `<span style="font-size:7px;font-weight:700;">${alt}</span>`;

  return `
  <div style="width:595px;height:842px;background:${bg};display:flex;flex-direction:column;
              align-items:center;justify-content:center;padding:40px;box-sizing:border-box;
              page-break-after:always;position:relative;">

    <!-- COVER HEADER -->
    <div style="position:absolute;top:24px;left:28px;right:28px;display:flex;align-items:center;justify-content:space-between;">
      <div style="display:flex;align-items:center;gap:8px;">
        ${logoImg(logos.nish, 32, "NISH")}
        <div style="font-family:Arial;font-size:6.5px;color:#555;">
          <div style="font-weight:700;color:#003366;">National Institute of Speech &amp; Hearing</div>
          <div>Thiruvananthapuram, Kerala</div>
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:8px;">
        ${logoImg(logos.ncaht, 26, "NCAHT")}
        ${logoImg(logos.icmr,  26, "ICMR")}
      </div>
    </div>

    <!-- COVER CARD -->
    <div style="border:4px solid ${border};border-radius:20px;padding:40px 36px;text-align:center;
                background:${page};box-shadow:0 8px 40px ${border}60;width:100%;max-width:460px;">
      ${firstImg
        ? `<div style="width:200px;height:200px;margin:0 auto 28px;border:3px solid ${border};border-radius:12px;overflow:hidden;">
            <img src="${firstImg}" style="width:100%;height:100%;object-fit:contain;" crossorigin="anonymous"/>
           </div>`
        : `<div style="font-size:80px;margin-bottom:20px;">📖</div>`}
      <div style="font-family:${titleFont};font-size:32px;font-weight:900;color:${accent};line-height:1.2;margin-bottom:14px;">${story.title||"Untitled Story"}</div>
      ${story.subtitle?`<div style="font-family:Georgia,serif;font-size:14px;color:${accent};opacity:.75;font-style:italic;margin-bottom:18px;">${story.subtitle}</div>`:""}
      <div style="width:60px;height:3px;background:${border};margin:0 auto 18px;border-radius:2px;"></div>
      <div style="font-family:Arial;font-size:11px;color:#888;">${story.author||"NISH Storybook Series"}</div>
      <div style="font-family:Arial;font-size:10px;color:#aaa;margin-top:6px;">Language: ${LANGUAGES.find(l=>l.code===story.language)?.name||story.language}</div>
    </div>

    <!-- COVER FOOTER -->
    <div style="position:absolute;bottom:22px;left:28px;right:28px;display:flex;justify-content:space-between;align-items:center;">
      <div style="display:flex;align-items:center;gap:8px;">
        ${logoImg(logos.nish,  22, "NISH")}
        ${logoImg(logos.ncaht, 18, "NCAHT")}
        ${logoImg(logos.icmr,  18, "ICMR")}
      </div>
      <div style="font-family:Arial;font-size:7px;color:#aaa;">© NISH · NCAHT · ICMR · Foundational Stage Learning Platform</div>
    </div>
  </div>`;
}

async function generateStoryPDF(story) {
  const themeObj = THEMES.find(t => t.id === story.theme) || THEMES[0];
  const fontPx   = FONT_SIZES.find(f => f.id === story.fontSize)?.size || 17;

  // Fetch all logos as base64
  const [nishB64, ncahtB64, icmrB64] = await Promise.all([
    imgToBase64(nishSrc),
    imgToBase64(ncahtSrc),
    imgToBase64(icmrSrc),
  ]);
  const logos = { nish: nishB64, ncaht: ncahtB64, icmr: icmrB64 };

  const total  = story.pages.length;
  const cover  = buildCoverHTML(story, themeObj, logos);
  const pages  = story.pages.map((p, i) => buildPageHTML(p, i+1, total, story, themeObj, fontPx, logos)).join("");

  const win = window.open("","_blank");
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#888;}
@media print{body{background:white;}@page{size:A4 portrait;margin:0;}
div,img{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style>
</head><body>${cover}${pages}</body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 700);
}

// ─── IndicatorTagPicker ───────────────────────────────────────────────────────
// Shared chip-picker for the 40 NCF indicators, grouped by domain. Self-contained
// copy of the same component used in CollectionManager / FlashcardGenerator /
// WorksheetCreator / ActivityCards — kept local here so this file stays
// drop-in deployable. (Candidate for extraction to a shared component file later.)
function IndicatorTagPicker({ taxonomy, selected, onToggle, compact }) {
  const [openDomain, setOpenDomain] = useState(null);
  if (!taxonomy) return (
    <div style={{ fontSize:11, color:"#aaa" }}>Loading indicator list…</div>
  );
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {taxonomy.domains.map(dom => {
        const inds = taxonomy.indicators.filter(i => i.domainId === dom.id);
        const selCount = inds.filter(i => selected.includes(i.id)).length;
        const open = openDomain === dom.id;
        return (
          <div key={dom.id} style={{ border:"1.5px solid #e0e7f0", borderRadius:9, overflow:"hidden" }}>
            <button onClick={() => setOpenDomain(open ? null : dom.id)}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:8, padding: compact ? "6px 10px" : "8px 12px",
                       background: selCount > 0 ? "#EBF4FB" : "white", border:"none", cursor:"pointer", textAlign:"left" }}>
              <span style={{ fontSize: compact ? 13 : 15 }}>{dom.icon}</span>
              <span style={{ flex:1, fontSize: compact ? 11 : 12, fontWeight:700, color:"#003366" }}>{dom.label}</span>
              {selCount > 0 && (
                <span style={{ fontSize:10, fontWeight:800, color:"#003366", background:"white",
                               border:"1px solid #BDD7EE", borderRadius:10, padding:"1px 7px" }}>{selCount}</span>
              )}
              <span style={{ fontSize:11, color:"#888" }}>{open ? "▲" : "▼"}</span>
            </button>
            {open && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5, padding:"10px 12px", background:"#FAFCFF" }}>
                {inds.map(ind => {
                  const on = selected.includes(ind.id);
                  return (
                    <button key={ind.id} onClick={() => onToggle(ind.id)}
                      title={ind.label}
                      style={{ padding:"4px 10px", borderRadius:8, fontSize:11, fontWeight:700,
                               border:`1.5px solid ${on ? "#003366" : "#e0e7f0"}`,
                               background: on ? "#003366" : "white",
                               color: on ? "white" : "#555", cursor:"pointer" }}>
                      {ind.id} {on ? "✓" : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── COLLECTION PICKER MODAL ──────────────────────────────────────────────────
function CollectionPickerModal({ getAuthHeader, onPick, onClose }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [picked,  setPicked]  = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const base = API.replace("/api","");
        const res  = await fetch(`${API}/collection?limit=200`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setItems(data.images.map(doc => ({
          id:       doc._id,
          name:     doc.title || "Image",
          imageUrl: `${base}${doc.url}`,
          relUrl:   doc.url,
          indicators: doc.indicators || [],
        })));
      } catch {}
      setLoading(false);
    })();
  }, []);

  const filtered = items.filter(it => !search || it.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div style={{background:"#f5f7fa",borderRadius:18,width:760,maxWidth:"96vw",maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 60px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"14px 20px 10px",borderBottom:"1px solid #e0e7f0",display:"flex",alignItems:"center",gap:10,background:"white",borderRadius:"18px 18px 0 0"}}>
          <div style={{fontWeight:800,fontSize:14,color:"#003366",flex:1}}>🗂️ Pick Image from Collection</div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
            style={{border:"1.5px solid #dce3f5",borderRadius:8,padding:"5px 10px",fontSize:12,outline:"none",width:180}}/>
          <button onClick={onClose} style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"5px 12px",cursor:"pointer",color:"#666"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:14}}>
          {loading ? <div style={{textAlign:"center",padding:60,fontSize:32,color:"#aaa"}}>⏳</div>
          : filtered.length===0 ? <div style={{textAlign:"center",padding:60,color:"#aaa"}}>No images found</div>
          : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(100px,1fr))",gap:8}}>
              {filtered.map(item=>{
                const on = picked?.id === item.id;
                return (
                  <div key={item.id} onClick={()=>setPicked(on ? null : item)}
                    style={{border:`2px solid ${on?"#003366":"#e0e7f0"}`,borderRadius:8,overflow:"hidden",cursor:"pointer",background:"white",position:"relative"}}>
                    {on && <div style={{position:"absolute",top:4,right:4,background:"#003366",color:"white",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:900,zIndex:2}}>✓</div>}
                    {item.indicators?.length > 0 && (
                      <div style={{position:"absolute",top:4,left:4,background:"rgba(0,51,102,.85)",color:"white",fontSize:8.5,fontWeight:800,padding:"1px 5px",borderRadius:5,zIndex:2}}
                        title={item.indicators.join(", ")}>
                        🎯 {item.indicators.length}
                      </div>
                    )}
                    <div style={{height:70,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f9fc"}}>
                      <img src={item.imageUrl} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}} alt=""/>
                    </div>
                    <div style={{padding:"3px 5px",fontSize:9,fontWeight:700,color:"#333",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                  </div>
                );
              })}
            </div>}
        </div>
        <div style={{padding:"10px 20px",borderTop:"1px solid #e0e7f0",background:"white",borderRadius:"0 0 18px 18px",display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onClose} style={{padding:"7px 16px",background:"white",color:"#666",border:"1px solid #dce3f5",borderRadius:8,fontWeight:600,fontSize:12,cursor:"pointer"}}>Cancel</button>
          <button onClick={()=>picked && onPick(picked)} disabled={!picked}
            style={{padding:"7px 18px",background:picked?"#003366":"#ccc",color:"white",border:"none",borderRadius:8,fontWeight:700,fontSize:12,cursor:picked?"pointer":"default"}}>
            Use This Image →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SAVED BOOKS PANEL ────────────────────────────────────────────────────────
function SavedBooksPanel({ getAuthHeader, onLoad, onClose }) {
  const [books,    setBooks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const s    = search ? `&search=${encodeURIComponent(search)}` : "";
      const res  = await fetch(`${API}/storybook?limit=50${s}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) setBooks(data.storyBooks);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [search]);

  const del = async id => {
    setDeleting(id);
    try {
      await fetch(`${API}/storybook/${id}`, { method:"DELETE", headers: getAuthHeader() });
      setBooks(p => p.filter(b => b._id !== id));
    } catch {}
    setDeleting(null);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div style={{background:"#f5f7fa",borderRadius:18,width:840,maxWidth:"96vw",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 60px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"16px 24px 12px",borderBottom:"1px solid #e0e7f0",display:"flex",alignItems:"center",gap:12,background:"white",borderRadius:"18px 18px 0 0"}}>
          <div style={{fontWeight:800,fontSize:15,color:"#003366",flex:1}}>📚 Saved Story Books</div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
            style={{border:"1.5px solid #dce3f5",borderRadius:8,padding:"6px 12px",fontSize:12,outline:"none",width:200}}/>
          <button onClick={onClose} style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"6px 12px",cursor:"pointer",color:"#666"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          {loading ? <div style={{textAlign:"center",padding:60,fontSize:32,color:"#aaa"}}>⏳</div>
          : books.length===0 ? <div style={{textAlign:"center",padding:60,color:"#aaa"}}>No saved story books yet</div>
          : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
              {books.map(b=>{
                const themeObj = THEMES.find(t=>t.id===b.theme)||THEMES[0];
                return (
                  <div key={b._id} style={{background:themeObj.bg,borderRadius:12,padding:16,border:`2px solid ${themeObj.border}`,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6}}>
                      <div style={{fontFamily:"Georgia,serif",fontWeight:900,fontSize:14,color:themeObj.accent,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{b.title}</div>
                      {b.indicators?.length > 0 && (
                        <span style={{fontSize:9,fontWeight:700,color:"#003366",background:"#EBF4FB",padding:"1px 6px",borderRadius:6,flexShrink:0}}>
                          🎯 {b.indicators.length}
                        </span>
                      )}
                    </div>
                    {b.subtitle && <div style={{fontSize:11,color:themeObj.accent,opacity:.7,fontStyle:"italic",marginBottom:4}}>{b.subtitle}</div>}
                    <div style={{fontSize:10,color:"#888",marginBottom:12}}>
                      {LANGUAGES.find(l=>l.code===b.language)?.name} · {THEMES.find(t=>t.id===b.theme)?.label} · Used {b.usedCount||0}×
                      <br/>{new Date(b.createdAt).toLocaleDateString()}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>onLoad(b)} style={{flex:1,padding:"7px 0",background:"#003366",color:"white",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Load & Edit</button>
                      <button onClick={()=>del(b._id)} style={{padding:"7px 12px",background:"#fff0f0",color:"#c0392b",border:"1px solid #f5c6c6",borderRadius:7,fontSize:12,cursor:"pointer"}}>
                        {deleting===b._id?"…":"🗑"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>}
        </div>
      </div>
    </div>
  );
}

// ─── MINI PAGE PREVIEW ────────────────────────────────────────────────────────
function MiniPagePreview({ page, story, themeObj, isActive, onClick, pageNum }) {
  const layout = page.layout || story.layout;
  const imgSrc = resolveImgUrl(page.imageUrl);
  const W = 131, H = 186;

  return (
    <div onClick={onClick} style={{
      width:W, height:H, background:themeObj.page,
      border:`2px solid ${isActive ? themeObj.accent : themeObj.border}`,
      borderRadius:5, overflow:"hidden", cursor:"pointer", flexShrink:0,
      boxShadow: isActive ? `0 0 0 3px ${themeObj.accent}60` : "0 1px 4px rgba(0,0,0,.1)",
      display:"flex", flexDirection:"column", padding:"5px 6px 4px", transition:"all .15s"
    }}>
      <div style={{height:5,background:themeObj.border,borderRadius:2,marginBottom:3,flexShrink:0}}/>
      <div style={{flex:1,display:"flex",gap:2,overflow:"hidden",minHeight:0}}>
        {(layout==="image-top"||layout==="image-bottom") && (
          <div style={{flex:1,display:"flex",flexDirection:layout==="image-top"?"column":"column-reverse",gap:2}}>
            {imgSrc && <div style={{flex:1.5,background:"#f0f0f0",borderRadius:2,overflow:"hidden"}}><img src={imgSrc} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/></div>}
            <div style={{flex:1,background:"#ececec",borderRadius:2}}/>
          </div>
        )}
        {(layout==="image-left"||layout==="image-right") && (
          <div style={{flex:1,display:"flex",flexDirection:layout==="image-left"?"row":"row-reverse",gap:2}}>
            {imgSrc && <div style={{flex:1,background:"#f0f0f0",borderRadius:2,overflow:"hidden"}}><img src={imgSrc} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/></div>}
            <div style={{flex:1,background:"#ececec",borderRadius:2}}/>
          </div>
        )}
        {layout==="image-full" && imgSrc && (
          <div style={{flex:1,background:"#f0f0f0",borderRadius:2,overflow:"hidden",position:"relative"}}>
            <img src={imgSrc} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
            <div style={{position:"absolute",bottom:0,left:0,right:0,height:"28%",background:"rgba(0,0,0,.4)"}}/>
          </div>
        )}
        {(layout==="text-only" || (!imgSrc && layout!=="image-full")) && <div style={{flex:1,background:"#ececec",borderRadius:2}}/>}
      </div>
      <div style={{height:3,background:themeObj.border,borderRadius:2,marginTop:3,flexShrink:0}}/>
      <div style={{textAlign:"center",fontSize:7,color:themeObj.accent,fontWeight:700,marginTop:2}}>{pageNum}</div>
    </div>
  );
}

// ─── PAGE EDITOR ──────────────────────────────────────────────────────────────
function PageEditor({ page, language, themeObj, onUpdate, onDelete, onMoveUp, onMoveDown, pageNum, total, onPickFromCollection }) {
  const fileRef = useRef();

  const handleImage = files => {
    const file = files[0];
    if (!file?.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = e => onUpdate({ ...page, imageUrl: e.target.result, collectionId: null });
    reader.readAsDataURL(file);
  };

  return (
    <div style={{background:"white",borderRadius:12,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,.07)",border:`1.5px solid ${themeObj.border}`}}>
      <div style={{background:themeObj.bg,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1.5px solid ${themeObj.border}`}}>
        <div style={{fontWeight:800,fontSize:13,color:themeObj.accent}}>Page {pageNum}</div>
        <div style={{display:"flex",gap:6}}>
          {pageNum>1     && <button onClick={onMoveUp}   style={{background:"#f0f4ff",color:"#003366",border:"none",borderRadius:6,padding:"4px 10px",fontSize:13,cursor:"pointer",fontWeight:700}}>↑</button>}
          {pageNum<total && <button onClick={onMoveDown} style={{background:"#f0f4ff",color:"#003366",border:"none",borderRadius:6,padding:"4px 10px",fontSize:13,cursor:"pointer",fontWeight:700}}>↓</button>}
          <button onClick={onDelete} style={{background:"#fff0f0",color:"#c0392b",border:"none",borderRadius:6,padding:"4px 10px",fontSize:13,cursor:"pointer",fontWeight:700}}>🗑</button>
        </div>
      </div>
      <div style={{padding:16,display:"grid",gridTemplateColumns:"170px 1fr",gap:16}}>
        {/* Image panel */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:"#888",marginBottom:6,letterSpacing:.5}}>IMAGE</div>
          <div onClick={()=>fileRef.current?.click()}
            onDrop={e=>{e.preventDefault();handleImage(e.dataTransfer.files);}}
            onDragOver={e=>e.preventDefault()}
            style={{height:120,border:`2px dashed ${themeObj.border}`,borderRadius:8,cursor:"pointer",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",background:themeObj.bg,position:"relative"}}>
            {page.indicators?.length > 0 && (
              <div style={{position:"absolute",top:5,left:5,background:"rgba(0,51,102,.85)",color:"white",fontSize:9,fontWeight:800,padding:"1px 6px",borderRadius:6,zIndex:2}}
                title={page.indicators.join(", ")}>
                🎯 {page.indicators.length}
              </div>
            )}
            {page.imageUrl
              ? <img src={resolveImgUrl(page.imageUrl)} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/>
              : <div style={{textAlign:"center",color:"#aaa",fontSize:12}}><div style={{fontSize:24,marginBottom:4}}>📷</div>Click or drop</div>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleImage(e.target.files)}/>
          <button onClick={onPickFromCollection}
            style={{width:"100%",marginTop:6,padding:"5px 8px",background:"white",border:`1.5px solid ${themeObj.border}`,borderRadius:7,fontSize:11,fontWeight:700,color:themeObj.accent,cursor:"pointer"}}>
            🗂️ From Collection
          </button>
          {page.imageUrl && (
            <button onClick={()=>onUpdate({...page,imageUrl:"",collectionId:null})}
              style={{width:"100%",marginTop:4,padding:"4px 8px",background:"#fff0f0",border:"none",borderRadius:7,fontSize:11,color:"#c0392b",cursor:"pointer"}}>
              ✕ Remove Image
            </button>
          )}
          <div style={{marginTop:10,fontSize:11,fontWeight:700,color:"#888",marginBottom:5,letterSpacing:.5}}>LAYOUT</div>
          <select value={page.layout||"image-top"} onChange={e=>onUpdate({...page,layout:e.target.value})}
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:6,padding:"6px 8px",fontSize:11,outline:"none",background:"white"}}>
            {LAYOUTS.map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </div>
        {/* Text panel */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:"#888",marginBottom:6,letterSpacing:.5}}>
            TEXT — {LANGUAGES.find(l=>l.code===language)?.name}
          </div>
          <textarea
            value={page.texts?.[language]||""}
            onChange={e=>onUpdate({...page,texts:{...page.texts,[language]:e.target.value}})}
            placeholder="Type the story text for this page..."
            rows={6}
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"10px 12px",fontSize:13,outline:"none",fontFamily:"Georgia,serif",lineHeight:1.7,resize:"vertical",boxSizing:"border-box"}}
          />
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function StoryBooks() {
  const { getAuthHeader } = useAuth();

  const [story,       setStory]       = useState(EMPTY_STORY);
  const [activePage,  setActivePage]  = useState(-1); // -1 = cover
  const [activeTab,   setActiveTab]   = useState("edit");
  const [editingLang, setEditingLang] = useState("en");
  const [showPicker,  setShowPicker]  = useState(false);
  const [pickerFor,   setPickerFor]   = useState(null);
  const [showSaved,   setShowSaved]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState("");
  const [savedId,     setSavedId]     = useState(null);

  const themeObj = THEMES.find(t => t.id === story.theme) || THEMES[0];
  const fontPx   = FONT_SIZES.find(f => f.id === story.fontSize)?.size || 17;

  // Shared NCF development taxonomy (40 indicators / 5 domains) — same source
  // as CollectionManager / FlashcardGenerator / WorksheetCreator / ActivityCards.
  // Used to tag this book (and its pages) so SM-2/BKT engagement can later
  // roll up to NCF indicator progress.
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API}/collection/taxonomy`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setTaxonomy(data.taxonomy);
      } catch {}
    })();
  }, []);

  const updateStory = updates => setStory(prev => ({ ...prev, ...updates }));

  // Book-level NCF indicator tags — the primary tag for this story book as a
  // whole. Individual pages can also carry their own tags (inherited from
  // Collection when picked).
  const toggleStoryIndicator = (id) => {
    setStory(prev => {
      const cur = prev.indicators || [];
      return { ...prev, indicators: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  };

  // Domains touched by this book right now (book tags ∪ every page's tags) —
  // purely a live preview; the server derives & stores the authoritative
  // `domains` field on save.
  const touchedDomains = (() => {
    if (!taxonomy) return [];
    const all = new Set(story.indicators || []);
    story.pages.forEach(p => (p.indicators || []).forEach(id => all.add(id)));
    const domainIds = new Set();
    taxonomy.indicators.forEach(ind => { if (all.has(ind.id)) domainIds.add(ind.domainId); });
    return taxonomy.domains.filter(d => domainIds.has(d.id));
  })();

  const updatePage = (idx, updates) => {
    const pages = [...story.pages];
    pages[idx] = { ...pages[idx], ...updates };
    updateStory({ pages });
  };

  const addPage = () => {
    const np = { id:`p${Date.now()}`, layout:story.layout, imageUrl:"", indicators:[], texts:{en:"",ml:"",hi:"",ta:"",te:""} };
    const pages = [...story.pages, np];
    updateStory({ pages });
    setActivePage(pages.length - 1);
  };

  const deletePage = idx => {
    if (story.pages.length <= 1) return;
    const pages = story.pages.filter((_,i) => i !== idx);
    updateStory({ pages });
    setActivePage(Math.min(activePage, pages.length - 1));
  };

  const movePage = (idx, dir) => {
    const pages = [...story.pages];
    const ni = idx + dir;
    if (ni < 0 || ni >= pages.length) return;
    [pages[idx], pages[ni]] = [pages[ni], pages[idx]];
    updateStory({ pages });
    setActivePage(ni);
  };

  const handlePickFromCollection = idx => {
    setPickerFor(idx);
    setShowPicker(true);
  };

  const handleCollectionPick = picked => {
    if (pickerFor !== null) {
      updatePage(pickerFor, { imageUrl: picked.relUrl, collectionId: picked.id, indicators: picked.indicators || [] });
    }
    setShowPicker(false);
    setPickerFor(null);
  };

  const handleLoadSaved = async (b) => {
    setShowSaved(false);
    try {
      const res  = await fetch(`${API}/storybook/${b._id}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        const sb = data.storyBook;
        setStory({
          title:           sb.title,
          subtitle:        sb.subtitle || "",
          author:          sb.author   || "NISH Storybook Series",
          language:        sb.language || "en",
          theme:           sb.theme    || "storybook",
          layout:          sb.layout   || "image-top",
          fontSize:        sb.fontSize || "md",
          showPageNumbers: sb.showPageNumbers !== false,
          indicators:      sb.indicators || [],
          pages: (sb.pages||[]).map(p => ({
            id:           p.pageId || p._id || `p${Math.random()}`,
            layout:       p.layout || "image-top",
            imageUrl:     p.imageUrl || "",
            collectionId: p.collectionId || null,
            indicators:   p.indicators || [],
            texts:        p.texts || { en:"" },
          })),
        });
        setSavedId(sb._id);
        setActivePage(-1);
      }
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const base = API.replace("/api","");
      const body = {
        title:           story.title || "Untitled Story",
        subtitle:        story.subtitle,
        author:          story.author,
        language:        story.language,
        theme:           story.theme,
        layout:          story.layout,
        fontSize:        story.fontSize,
        showPageNumbers: story.showPageNumbers,
        indicators:      story.indicators || [],
        pages: story.pages.map(p => ({
          pageId:       p.id,
          layout:       p.layout,
          imageUrl:     p.imageUrl?.startsWith(base) ? p.imageUrl.replace(base,"") : (p.imageUrl||""),
          collectionId: p.collectionId || null,
          indicators:   p.indicators || [],
          texts:        p.texts || {},
        })),
      };
      const method = savedId ? "PUT"  : "POST";
      const url    = savedId ? `${API}/storybook/${savedId}` : `${API}/storybook`;
      const res    = await fetch(url, { method, headers: getAuthHeader(), body: JSON.stringify(body) });
      const data   = await res.json();
      if (data.success) { setSavedId(data.storyBook._id); setSaveMsg("✅ Saved!"); }
      else setSaveMsg(`❌ ${data.message||"Save failed"}`);
    } catch { setSaveMsg("❌ Backend not reachable"); }
    setSaving(false);
    setTimeout(() => setSaveMsg(""), 5000);
  };

  const handlePrint = async () => {
    await generateStoryPDF(story);
    if (savedId) { try { await fetch(`${API}/storybook/${savedId}/use`, { method:"POST" }); } catch {} }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{fontFamily:"'Segoe UI',Arial,sans-serif",background:themeObj.bg,minHeight:"100vh",color:themeObj.text,transition:"background .3s"}}>

      {/* TOP BAR */}
      <div style={{background:"#003366",color:"white",padding:"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:56}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{background:"white",color:"#003366",fontWeight:900,fontSize:13,padding:"3px 8px",borderRadius:4}}>NISH</div>
          <div style={{height:24,width:1,background:"rgba(255,255,255,.3)"}}/>
          <div style={{fontWeight:700,fontSize:14}}>Foundational Stage Learning Platform</div>
          <div style={{background:"rgba(255,255,255,.15)",fontSize:11,padding:"2px 10px",borderRadius:10}}>📖 Story Book Builder</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{background:"#b30000",fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:3}}>NCAHT</div>
          <div style={{background:"#006400",fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:3}}>ICMR</div>
          <button onClick={()=>setShowSaved(true)}
            style={{background:"rgba(255,255,255,.15)",color:"white",border:"1px solid rgba(255,255,255,.3)",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",marginLeft:8}}>
            📚 Saved Books
          </button>
          {saveMsg && <span style={{fontSize:11,fontWeight:700,color:saveMsg.startsWith("✅")?"#7dffb3":"#ffb3b3",maxWidth:240}}>{saveMsg}</span>}
          <button onClick={handleSave} disabled={saving}
            style={{background:saving?"#555":"rgba(255,255,255,.2)",color:"white",border:"1px solid rgba(255,255,255,.3)",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:saving?"default":"pointer"}}>
            {saving?"Saving…":"💾 Save"}
          </button>
          <button onClick={handlePrint}
            style={{background:"white",color:"#003366",border:"none",borderRadius:6,padding:"6px 16px",fontWeight:800,fontSize:12,cursor:"pointer",marginLeft:4}}>
            🖨️ Print PDF
          </button>
        </div>
      </div>

      {/* TABS */}
      <div style={{background:"white",borderBottom:`2px solid ${themeObj.border}`,padding:"0 24px",display:"flex"}}>
        {[["edit","✏️ Edit Pages"],["settings","⚙️ Book Settings"],["export","🖨️ Export"]].map(([tab,label])=>(
          <button key={tab} onClick={()=>setActiveTab(tab)} style={{
            background:"none",border:"none",padding:"13px 20px",fontSize:13,fontWeight:700,
            color:activeTab===tab?themeObj.accent:"#888",
            borderBottom:`3px solid ${activeTab===tab?themeObj.accent:"transparent"}`,cursor:"pointer"
          }}>{label}</button>
        ))}
        <div style={{flex:1}}/>
        <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#888"}}>
          <span style={{fontWeight:700,color:themeObj.accent}}>{story.pages.length}</span> pages ·
          <span style={{fontWeight:700,color:themeObj.accent}}>{THEMES.find(t=>t.id===story.theme)?.label}</span> theme
          {savedId && <span style={{background:"#e8f0fe",color:"#003366",padding:"2px 8px",borderRadius:6,fontSize:11,fontWeight:700,marginLeft:6}}>📌 Saved</span>}
        </div>
      </div>

      {/* ── EDIT PAGES ── */}
      {activeTab==="edit" && (
        <div style={{display:"grid",gridTemplateColumns:"196px 1fr",height:"calc(100vh - 108px)"}}>

          {/* Page strip */}
          <div style={{background:"white",borderRight:`2px solid ${themeObj.border}`,display:"flex",flexDirection:"column",overflowY:"auto"}}>
            <div style={{padding:12,borderBottom:`1px solid ${themeObj.border}40`}}>
              <div style={{fontSize:10,fontWeight:800,color:"#888",letterSpacing:.8,marginBottom:8}}>EDITING IN</div>
              <select value={editingLang} onChange={e=>setEditingLang(e.target.value)}
                style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:6,padding:"5px 6px",fontSize:11,outline:"none"}}>
                {LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.name}</option>)}
              </select>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"10px 12px",display:"flex",flexDirection:"column",gap:10,alignItems:"center"}}>
              {/* Cover */}
              <div onClick={()=>setActivePage(-1)} style={{
                width:"100%",border:`2px solid ${activePage===-1?themeObj.accent:themeObj.border}`,
                borderRadius:6,padding:"6px 10px",cursor:"pointer",
                background:activePage===-1?themeObj.bg:"white",
                fontSize:11,fontWeight:700,color:activePage===-1?themeObj.accent:"#888",textAlign:"center"
              }}>📖 Cover</div>
              {story.pages.map((page,i)=>(
                <MiniPagePreview key={page.id} page={page} story={story} themeObj={themeObj}
                  isActive={activePage===i} onClick={()=>setActivePage(i)} pageNum={i+1}/>
              ))}
              <button onClick={addPage} style={{
                width:"100%",border:`2px dashed ${themeObj.border}`,borderRadius:8,padding:10,
                background:"none",color:themeObj.accent,fontWeight:700,fontSize:13,cursor:"pointer"
              }}>+ Add Page</button>
            </div>
          </div>

          {/* Editor area */}
          <div style={{overflowY:"auto",padding:24,display:"flex",gap:24}}>

            {activePage===-1 ? (
              /* Cover editor */
              <div style={{flex:1,maxWidth:680}}>
                <div style={{fontWeight:800,fontSize:18,color:themeObj.accent,marginBottom:20}}>📖 Book Cover</div>
                {/* Empty state hint */}
                {!story.title && (
                  <div style={{background:themeObj.bg,border:`1.5px dashed ${themeObj.border}`,borderRadius:10,padding:"14px 18px",marginBottom:16,fontSize:13,color:themeObj.accent}}>
                    👋 Start by entering your story title below, then add pages using the panel on the left.
                  </div>
                )}
                <div style={{background:"white",borderRadius:12,padding:24,boxShadow:"0 2px 10px rgba(0,0,0,.07)",display:"flex",flexDirection:"column",gap:14}}>
                  {[
                    {label:"Story Title",     key:"title",    placeholder:"Enter story title"},
                    {label:"Subtitle",        key:"subtitle", placeholder:"A story about kindness..."},
                    {label:"Author / Series", key:"author",   placeholder:"NISH Storybook Series"},
                  ].map(f=>(
                    <div key={f.key}>
                      <div style={{fontSize:11,fontWeight:700,color:"#888",marginBottom:6,letterSpacing:.5}}>{f.label.toUpperCase()}</div>
                      <input value={story[f.key]||""} onChange={e=>updateStory({[f.key]:e.target.value})} placeholder={f.placeholder}
                        style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"10px 12px",fontSize:14,outline:"none",fontFamily:"Georgia,serif",boxSizing:"border-box"}}/>
                    </div>
                  ))}
                </div>
                {/* Cover mini preview */}
                <div style={{marginTop:20}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#888",marginBottom:10,letterSpacing:.5}}>PREVIEW</div>
                  <div style={{width:200,height:282,background:themeObj.bg,border:`3px solid ${themeObj.border}`,borderRadius:10,
                    display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:16,boxShadow:"0 6px 30px rgba(0,0,0,.15)"}}>
                    {story.pages[0]?.imageUrl
                      ? <div style={{width:80,height:80,border:`2px solid ${themeObj.border}`,borderRadius:6,overflow:"hidden",marginBottom:10}}><img src={resolveImgUrl(story.pages[0].imageUrl)} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/></div>
                      : <div style={{fontSize:36,marginBottom:10}}>📖</div>}
                    <div style={{fontFamily:"Georgia,serif",fontWeight:900,fontSize:13,color:themeObj.accent,textAlign:"center",lineHeight:1.2,marginBottom:6}}>{story.title||"Your Story Title"}</div>
                    {story.subtitle && <div style={{fontFamily:"Georgia,serif",fontSize:9,color:themeObj.accent,opacity:.7,fontStyle:"italic",textAlign:"center"}}>{story.subtitle}</div>}
                    <div style={{marginTop:8,fontSize:8,color:"#aaa",textAlign:"center"}}>{story.author}</div>
                  </div>
                </div>
              </div>
            ) : (
              /* Page editor */
              <div style={{flex:1,maxWidth:680}}>
                <div style={{fontWeight:800,fontSize:18,color:themeObj.accent,marginBottom:20}}>
                  Page {activePage+1} of {story.pages.length}
                </div>
                <PageEditor
                  page={story.pages[activePage]}
                  language={editingLang}
                  themeObj={themeObj}
                  onUpdate={u=>updatePage(activePage,u)}
                  onDelete={()=>deletePage(activePage)}
                  onMoveUp={()=>movePage(activePage,-1)}
                  onMoveDown={()=>movePage(activePage,1)}
                  pageNum={activePage+1}
                  total={story.pages.length}
                  onPickFromCollection={()=>handlePickFromCollection(activePage)}
                />
                {/* All-language translations */}
                <div style={{marginTop:16,background:"white",borderRadius:12,padding:18,boxShadow:"0 2px 10px rgba(0,0,0,.07)"}}>
                  <div style={{fontWeight:700,fontSize:11,color:"#888",marginBottom:10,letterSpacing:.5}}>ALL LANGUAGE TRANSLATIONS</div>
                  <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                    {LANGUAGES.map(l=>(
                      <button key={l.code} onClick={()=>setEditingLang(l.code)} style={{
                        padding:"5px 10px",border:`1.5px solid ${editingLang===l.code?themeObj.accent:"#dce3f5"}`,
                        borderRadius:6,background:editingLang===l.code?themeObj.bg:"white",
                        cursor:"pointer",fontSize:11,fontWeight:700,color:editingLang===l.code?themeObj.accent:"#666"
                      }}>{l.name}</button>
                    ))}
                  </div>
                  <textarea
                    value={story.pages[activePage]?.texts?.[editingLang]||""}
                    onChange={e=>updatePage(activePage,{texts:{...story.pages[activePage].texts,[editingLang]:e.target.value}})}
                    placeholder={`Type story text in ${LANGUAGES.find(l=>l.code===editingLang)?.name}...`}
                    rows={4}
                    style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"10px 12px",fontSize:14,outline:"none",fontFamily:"Georgia,serif",lineHeight:1.7,resize:"vertical",boxSizing:"border-box"}}
                  />
                </div>
              </div>
            )}

            {/* A4 live preview panel */}
            <div style={{width:254,flexShrink:0}}>
              <div style={{fontWeight:700,fontSize:11,color:"#888",letterSpacing:.5,marginBottom:10}}>A4 PREVIEW</div>
              {activePage===-1 ? (
                <div style={{width:200,height:282,background:themeObj.bg,border:`3px solid ${themeObj.border}`,borderRadius:8,
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  padding:14,boxShadow:"0 4px 20px rgba(0,0,0,.12)"}}>
                  <div style={{fontSize:36,marginBottom:8}}>📖</div>
                  <div style={{fontFamily:"Georgia,serif",fontWeight:900,fontSize:13,color:themeObj.accent,textAlign:"center"}}>{story.title||"Your Title"}</div>
                  {story.subtitle && <div style={{fontFamily:"Georgia,serif",fontSize:9,color:themeObj.accent,opacity:.7,fontStyle:"italic",textAlign:"center",marginTop:5}}>{story.subtitle}</div>}
                </div>
              ) : (
                <div style={{position:"relative",width:249,height:353,overflow:"hidden",boxShadow:"0 4px 20px rgba(0,0,0,.15)",borderRadius:4}}>
                  <div style={{transform:"scale(0.418)",transformOrigin:"top left",width:595,height:842,pointerEvents:"none"}}
                    dangerouslySetInnerHTML={{__html: buildPageHTML(story.pages[activePage], activePage+1, story.pages.length, story, themeObj, fontPx)}}/>
                </div>
              )}
              <div style={{marginTop:8,fontSize:10,color:"#aaa",textAlign:"center"}}>
                {activePage===-1?"Cover":`Page ${activePage+1}`} · A4 Portrait
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS ── */}
      {activeTab==="settings" && (
        <div style={{padding:24,maxWidth:700,margin:"0 auto"}}>
          <div style={{fontWeight:800,fontSize:18,color:themeObj.accent,marginBottom:20}}>⚙️ Book Settings</div>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>

            <div style={{background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <div style={{fontWeight:700,fontSize:11,color:"#888",letterSpacing:.8,marginBottom:12}}>🌐 READING LANGUAGE (PDF output)</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                {LANGUAGES.map(l=>(
                  <button key={l.code} onClick={()=>updateStory({language:l.code})} style={{
                    padding:"8px 6px",border:story.language===l.code?`2px solid ${themeObj.accent}`:"1.5px solid #dce3f5",
                    borderRadius:8,background:story.language===l.code?themeObj.bg:"white",
                    cursor:"pointer",fontSize:11,fontWeight:700,color:story.language===l.code?themeObj.accent:"#555"
                  }}>{l.name}</button>
                ))}
              </div>
            </div>

            <div style={{background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <div style={{fontWeight:700,fontSize:11,color:"#888",letterSpacing:.8,marginBottom:12}}>🎨 BOOK THEME</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {THEMES.map(t=>(
                  <button key={t.id} onClick={()=>updateStory({theme:t.id})} style={{
                    padding:"10px 12px",border:story.theme===t.id?`2px solid ${t.accent}`:"1.5px solid #dce3f5",
                    borderRadius:10,background:t.bg,cursor:"pointer",display:"flex",alignItems:"center",gap:8
                  }}>
                    <div style={{width:18,height:18,borderRadius:"50%",background:t.accent,border:`2px solid ${t.border}`}}/>
                    <span style={{fontSize:12,fontWeight:700,color:t.text}}>{t.label}</span>
                    {story.theme===t.id && <span style={{fontSize:11,color:t.accent,marginLeft:"auto"}}>✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div style={{background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <div style={{fontWeight:700,fontSize:11,color:"#888",letterSpacing:.8,marginBottom:12}}>📐 DEFAULT PAGE LAYOUT</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {LAYOUTS.map(l=>(
                  <button key={l.id} onClick={()=>updateStory({layout:l.id})} style={{
                    padding:"10px 8px",border:story.layout===l.id?`2px solid ${themeObj.accent}`:"1.5px solid #dce3f5",
                    borderRadius:8,background:story.layout===l.id?themeObj.bg:"white",cursor:"pointer",textAlign:"center"
                  }}>
                    <div style={{fontSize:14,marginBottom:3}}>{l.icon}</div>
                    <div style={{fontSize:10,fontWeight:700,color:story.layout===l.id?themeObj.accent:"#555"}}>{l.label}</div>
                    <div style={{fontSize:9,color:"#aaa",marginTop:2}}>{l.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <div style={{fontWeight:700,fontSize:11,color:"#888",letterSpacing:.8,marginBottom:12}}>🔠 TEXT SIZE</div>
              <div style={{display:"flex",gap:8}}>
                {FONT_SIZES.map(fs=>(
                  <button key={fs.id} onClick={()=>updateStory({fontSize:fs.id})} style={{
                    flex:1,padding:"10px 8px",border:story.fontSize===fs.id?`2px solid ${themeObj.accent}`:"1.5px solid #dce3f5",
                    borderRadius:8,background:story.fontSize===fs.id?themeObj.bg:"white",
                    cursor:"pointer",textAlign:"center",fontSize:12,fontWeight:700,
                    color:story.fontSize===fs.id?themeObj.accent:"#555"
                  }}>{fs.label}<br/><span style={{fontSize:10,fontWeight:400,color:"#aaa"}}>{fs.size}px</span></button>
                ))}
              </div>
            </div>

            <div style={{background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <label style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                <input type="checkbox" checked={story.showPageNumbers} onChange={e=>updateStory({showPageNumbers:e.target.checked})} style={{width:18,height:18,accentColor:themeObj.accent}}/>
                <span style={{fontSize:14,fontWeight:600,color:"#555"}}>Show page numbers in footer</span>
              </label>
            </div>

            <div style={{background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
              <div style={{fontWeight:700,fontSize:11,color:"#888",letterSpacing:.8,marginBottom:4}}>🎯 DEVELOPMENT INDICATORS (NCF)</div>
              <div style={{fontSize:11,color:"#aaa",marginBottom:12,lineHeight:1.6}}>
                What does this story book, as a whole, help practice? Individual
                pages can carry their own tags too (inherited from Collection
                when picked).
              </div>
              <IndicatorTagPicker taxonomy={taxonomy} selected={story.indicators || []} onToggle={toggleStoryIndicator}/>
              {(story.indicators || []).length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:10}}>
                  {story.indicators.map(id => {
                    const ind = taxonomy?.indicators.find(i => i.id === id);
                    return (
                      <span key={id} style={{background:"#EBF4FB",color:"#003366",fontSize:11,
                                              fontWeight:700,padding:"3px 9px",borderRadius:10}}>
                        {id}{ind ? ` · ${ind.label}` : ""}
                      </span>
                    );
                  })}
                </div>
              )}
              {touchedDomains.length > 0 && (
                <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #f0f4ff"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#8fa5bc",textTransform:"uppercase",
                                letterSpacing:.6,marginBottom:6}}>Domains touched</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {touchedDomains.map(d => (
                      <span key={d.id} title={d.label}
                        style={{fontSize:12,fontWeight:700,color:"#003366",background:"#EBF4FB",
                                padding:"3px 10px",borderRadius:9}}>
                        {d.icon} {d.short}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── EXPORT ── */}
      {activeTab==="export" && (
        <div style={{padding:24,maxWidth:640,margin:"0 auto"}}>
          <div style={{fontWeight:800,fontSize:20,color:themeObj.accent,marginBottom:6}}>🖨️ Export Story Book</div>
          <div style={{fontSize:13,color:"#888",marginBottom:22}}>Exports as A4 PDF — cover page + one page per story page.</div>

          <div style={{background:"white",borderRadius:14,padding:26,boxShadow:"0 2px 16px rgba(0,0,0,.08)",marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
              <div style={{width:56,height:72,background:themeObj.bg,border:`3px solid ${themeObj.border}`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>📖</div>
              <div>
                <div style={{fontFamily:"Georgia,serif",fontWeight:900,fontSize:20,color:themeObj.accent}}>{story.title||"Untitled Story"}</div>
                {story.subtitle && <div style={{fontFamily:"Georgia,serif",fontSize:12,color:themeObj.accent,opacity:.7,fontStyle:"italic"}}>{story.subtitle}</div>}
                <div style={{fontSize:11,color:"#aaa",marginTop:3}}>{story.author}</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"5px 20px"}}>
              {[
                ["Total Pages", `${story.pages.length+1} (incl. cover)`],
                ["Language",   LANGUAGES.find(l=>l.code===story.language)?.name],
                ["Theme",      THEMES.find(t=>t.id===story.theme)?.label],
                ["Text Size",  FONT_SIZES.find(f=>f.id===story.fontSize)?.label],
                ["Page Size",  "A4 Portrait"],
                ["Logos",      "NISH · NCAHT · ICMR"],
              ].map(([k,v])=>(
                <div key={k} style={{display:"flex",gap:8,padding:"6px 0",borderBottom:"1px solid #f0f4ff",fontSize:13}}>
                  <span style={{color:"#aaa",minWidth:90}}>{k}:</span>
                  <span style={{fontWeight:700}}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Page thumbs */}
          <div style={{background:"white",borderRadius:12,padding:14,marginBottom:20,boxShadow:"0 1px 6px rgba(0,0,0,.06)"}}>
            <div style={{fontWeight:700,fontSize:11,color:"#888",marginBottom:10,letterSpacing:.5}}>PAGES IN THIS BOOK</div>
            {story.pages.length === 0 ? (
              <div style={{textAlign:"center",padding:20,color:"#aaa",fontSize:12}}>No pages yet — add pages in the Edit tab</div>
            ) : (
              <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:8}}>
                {story.pages.map((page,i)=>(
                  <MiniPagePreview key={page.id} page={page} story={story} themeObj={themeObj}
                    isActive={false} onClick={()=>{setActiveTab("edit");setActivePage(i);}} pageNum={i+1}/>
                ))}
              </div>
            )}
          </div>

          <button onClick={handlePrint} style={{
            width:"100%",padding:18,background:themeObj.accent,color:"white",
            border:"none",borderRadius:12,fontSize:16,fontWeight:800,cursor:"pointer",
            boxShadow:`0 4px 20px ${themeObj.accent}44`
          }}>
            📖 Generate &amp; Print — {story.pages.length+1} pages (incl. cover)
          </button>
          <div style={{textAlign:"center",fontSize:11,color:"#888",marginTop:10}}>
            Print dialog will open · Choose "Save as PDF" · A4 Portrait
          </div>
        </div>
      )}

      {/* Modals */}
      {showPicker && <CollectionPickerModal getAuthHeader={getAuthHeader} onPick={handleCollectionPick} onClose={()=>{setShowPicker(false);setPickerFor(null);}}/>}
      {showSaved  && <SavedBooksPanel       getAuthHeader={getAuthHeader} onLoad={handleLoadSaved}     onClose={()=>setShowSaved(false)}/>}
    </div>
  );
}